"use client";

import { useCallback, useState } from "react";
import type { ItinerarySuccess } from "@/lib/planner/api-types";
import type { PlannerPreferences } from "@/lib/planner/preferences";

type Props = {
  slug: string;
  areaName: string;
  preferences: PlannerPreferences;
  plan: ItinerarySuccess;
};

export function SharePlan({ slug, areaName, preferences, plan }: Props) {
  const [shareState, setShareState] = useState<"idle" | "saving" | "copied" | "error">("idle");
  const [exporting, setExporting] = useState(false);

  const onShare = useCallback(async () => {
    if (shareState === "saving") return;
    setShareState("saving");
    try {
      const res = await fetch(`/api/planner/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          scoredDate: plan.scoredDate,
          preferences,
          payload: { itinerary: plan.itinerary, route: plan.route, stops: plan.stops },
        }),
      });
      if (!res.ok) throw new Error("save failed");
      const { id } = (await res.json()) as { id: string };
      // Carry the sport so a shared MTB plan opens with MTB vocabulary on the
      // map and timeline (ski links are unchanged).
      const sportParam = preferences.sport === "mtb" ? "&sport=mtb" : "";
      const url = `${window.location.origin}/conditions/${slug}/planner?plan=${id}${sportParam}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareState("copied");
      } catch {
        // Clipboard blocked: drop the id into the URL bar so it is still shareable.
        window.history.replaceState(null, "", `?plan=${id}${sportParam}`);
        setShareState("copied");
      }
      window.setTimeout(() => setShareState("idle"), 2500);
    } catch {
      setShareState("error");
      window.setTimeout(() => setShareState("idle"), 2500);
    }
  }, [shareState, slug, plan, preferences]);

  const onExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { renderPlanImage } = await import("@/lib/planner/share-image");
      const pngBlob = await renderPlanImage(areaName, slug, preferences, plan);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(pngBlob);
      a.download = `peakwave-${slug}-day.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // Non-fatal: leave the UI as-is.
    } finally {
      setExporting(false);
    }
  }, [exporting, areaName, slug, preferences, plan]);

  return (
    <div className="mt-4 flex flex-wrap gap-2" data-export-ignore>
      <button
        type="button"
        onClick={onShare}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-sm bg-navy px-4 text-[13px] font-semibold text-white no-underline transition-colors hover:bg-navy-hover"
      >
        {shareState === "copied"
          ? "Link copied"
          : shareState === "saving"
            ? "Saving..."
            : shareState === "error"
              ? "Try again"
              : "Share this plan"}
      </button>
      <button
        type="button"
        onClick={onExport}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-sm border border-border-light bg-white px-4 text-[13px] font-semibold text-navy transition-colors hover:border-gold/40"
      >
        {exporting ? "Saving image..." : "Save as image"}
      </button>
    </div>
  );
}
